import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isToday, addMonths, subMonths,
  isSameDay,
} from "date-fns";
import {
  ChevronLeft, ChevronRight, CalendarDays,
  MapPin, ArrowRight, Package, User, Truck, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAY_HEADERS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/* ── Status badge ── */
function StatusBadge({ status }) {
  const cls =
    status === "Completed"  ? "bg-green-50 text-green-700 border-green-200"  :
    status === "In Transit" ? "bg-blue-50 text-blue-700 border-blue-200"     :
                              "bg-violet-50 text-violet-700 border-violet-200";
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${cls}`}>
      {status || "Scheduled"}
    </span>
  );
}

/* ── Transfer detail dialog ── */
function TransferDetailDialog({ open, onClose, transfer }) {
  if (!transfer) return null;
  const loads = transfer.load_details || [];

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl rounded-none p-0 gap-0">
        <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
          <DialogTitle className="text-white font-bold tracking-wide text-base">Transfer Details</DialogTitle>
          <p className="text-white/60 text-xs mt-0.5">
            {transfer.transfer_date
              ? format(new Date(transfer.transfer_date + "T00:00:00"), "dd MMM yyyy")
              : "—"}
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Route */}
          <div className="flex items-center gap-2 py-3 px-4 bg-[#F8FAFC] border border-[#E2E8F0]">
            <MapPin size={14} className="text-[#1B2A6B] shrink-0" />
            <span className="font-semibold text-[#0F172A] text-sm">{transfer.origin_label}</span>
            <ArrowRight size={14} className="text-[#CBD5E1] shrink-0" />
            <span className="font-semibold text-[#0F172A] text-sm">{transfer.destination_label}</span>
          </div>

          {/* Meta grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-0.5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B]">Customer</p>
              <div className="flex items-center gap-1.5 text-sm text-[#0F172A]">
                <User size={12} className="text-[#94A3B8]" />
                <span>{transfer.customer_name || "—"}</span>
              </div>
            </div>
            <div className="space-y-0.5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B]">Status</p>
              <StatusBadge status={transfer.status} />
            </div>
            <div className="space-y-0.5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B]">Vehicle</p>
              <div className="flex items-center gap-1.5 text-sm text-[#0F172A]">
                <Truck size={12} className="text-[#94A3B8]" />
                <span>{transfer.vehicle_display || "—"}</span>
              </div>
            </div>
            <div className="space-y-0.5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B]">Items</p>
              <div className="flex items-center gap-1.5 text-sm text-[#0F172A]">
                <Package size={12} className="text-[#94A3B8]" />
                <span>{(transfer.load_ids || []).length} item{(transfer.load_ids || []).length !== 1 ? "s" : ""}</span>
              </div>
            </div>
          </div>

          {/* Loads */}
          {loads.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B]">Items Transferred</p>
              <div className="border border-[#E2E8F0] divide-y divide-[#F1F5F9]">
                {loads.map((l, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                    <span className="font-mono text-xs text-[#1B2A6B] font-bold w-20 shrink-0">{l.load_ref || `#${i + 1}`}</span>
                    <span className="text-[#0F172A]">{l.load_name || "—"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] flex justify-end">
          <Button
            onClick={onClose}
            className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700]"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Main page ── */
export default function TransferReportPage() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [allTransfers, setAllTransfers] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewTransfer, setViewTransfer] = useState(null);

  /* Fetch all transfers once */
  useEffect(() => {
    axios.get(`${API}/consignments`, { withCredentials: true })
      .then(r => setAllTransfers(r.data))
      .catch(() => toast.error("Failed to load transfers."))
      .finally(() => setLoading(false));
  }, []);

  /* ── Calendar grid helpers ── */
  const monthStart = startOfMonth(currentMonth);
  const monthEnd   = endOfMonth(currentMonth);
  const gridStart  = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd    = endOfWeek(monthEnd,     { weekStartsOn: 1 });
  const days       = eachDayOfInterval({ start: gridStart, end: gridEnd });

  /* Count transfers per date string (YYYY-MM-DD) for the visible month */
  const countByDate = useMemo(() => {
    const map = {};
    for (const t of allTransfers) {
      if (!t.transfer_date) continue;
      map[t.transfer_date] = (map[t.transfer_date] || 0) + 1;
    }
    return map;
  }, [allTransfers]);

  /* Transfers on the selected date */
  const selectedDateTransfers = useMemo(() => {
    if (!selectedDate) return [];
    const ds = format(selectedDate, "yyyy-MM-dd");
    return allTransfers.filter(t => t.transfer_date === ds);
  }, [selectedDate, allTransfers]);

  /* Total transfers this month */
  const monthTotal = useMemo(() => {
    const monthStr = format(currentMonth, "yyyy-MM");
    return allTransfers.filter(t => t.transfer_date?.startsWith(monthStr)).length;
  }, [allTransfers, currentMonth]);

  const handleDayClick = (day) => {
    const ds = format(day, "yyyy-MM-dd");
    if (!countByDate[ds]) return;          // no transfers on this day
    setSelectedDate(day);
  };

  /* When month changes, clear selected date */
  const goToPrev = () => { setCurrentMonth(m => subMonths(m, 1)); setSelectedDate(null); };
  const goToNext = () => { setCurrentMonth(m => addMonths(m, 1)); setSelectedDate(null); };

  return (
    <div className="space-y-5" data-testid="transfer-report-page">

      {/* Page header */}
      <div>
        <h2 className="text-xl font-bold text-[#1B2A6B]">Consignment Report</h2>
        <p className="text-xs text-[#475569] mt-0.5">Monthly view of all transfer activity</p>
      </div>

      {/* Calendar card */}
      <div className="bg-white border border-[#E2E8F0]">

        {/* Month nav */}
        <div className="flex items-center justify-between px-6 py-4 bg-[#1B2A6B] border-b-4 border-[#FFD700]">
          <button
            onClick={goToPrev}
            className="p-1.5 rounded-sm text-white/60 hover:text-white hover:bg-white/10 transition-colors"
          >
            <ChevronLeft size={18} />
          </button>

          <div className="flex items-center gap-3">
            <CalendarDays size={16} className="text-[#FFD700]" />
            <h3 className="text-base font-bold text-white tracking-wide">
              {format(currentMonth, "MMMM yyyy")}
            </h3>
            {!loading && monthTotal > 0 && (
              <span className="bg-[#FFD700] text-[#1B2A6B] text-[10px] font-black px-2 py-0.5 rounded-sm">
                {monthTotal} transfer{monthTotal !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          <button
            onClick={goToNext}
            className="p-1.5 rounded-sm text-white/60 hover:text-white hover:bg-white/10 transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-[#E2E8F0]">
          {DAY_HEADERS.map(d => (
            <div key={d} className="py-2.5 text-center text-[10px] font-bold uppercase tracking-widest text-[#475569] bg-[#F9FAFB]">
              {d}
            </div>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-24 text-sm text-[#94A3B8]">Loading...</div>
        ) : (
          <div className="grid grid-cols-7">
            {days.map((day, idx) => {
              const ds          = format(day, "yyyy-MM-dd");
              const count       = countByDate[ds] || 0;
              const inMonth     = isSameMonth(day, currentMonth);
              const todayCell   = isToday(day);
              const isSelected  = selectedDate && isSameDay(day, selectedDate);
              const hasEvents   = count > 0;
              const isLastCol   = (idx + 1) % 7 === 0;

              return (
                <div
                  key={idx}
                  onClick={() => handleDayClick(day)}
                  className={`min-h-[90px] border-b border-[#E2E8F0] p-2 flex flex-col transition-colors
                    ${isLastCol ? "" : "border-r"}
                    ${isSelected  ? "bg-[#EEF2FF] ring-2 ring-inset ring-[#1B2A6B]" :
                      hasEvents && inMonth ? "cursor-pointer hover:bg-[#F5F7FF]" :
                      inMonth     ? "bg-white" : "bg-[#F9FAFB]"}
                  `}
                >
                  {/* Date number */}
                  <div className="flex justify-start mb-1.5">
                    <span className={`w-7 h-7 flex items-center justify-center text-xs font-bold rounded-full transition-colors
                      ${todayCell   ? "bg-[#1B2A6B] text-white" :
                        isSelected  ? "bg-[#1B2A6B] text-white" :
                        inMonth     ? "text-[#0F172A]" : "text-[#CBD5E1]"}
                    `}>
                      {format(day, "d")}
                    </span>
                  </div>

                  {/* Transfer count badge */}
                  {hasEvents && inMonth && (
                    <div className="mt-auto">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold rounded-sm border
                        ${isSelected
                          ? "bg-[#1B2A6B] text-white border-[#1B2A6B]"
                          : "bg-violet-50 text-violet-700 border-violet-200"}
                      `}>
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isSelected ? "bg-[#FFD700]" : "bg-violet-500"}`} />
                        {count} transfer{count !== 1 ? "s" : ""}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Hint when nothing selected */}
      {!loading && !selectedDate && monthTotal > 0 && (
        <p className="text-xs text-[#94A3B8] text-center">
          Click on any highlighted date to see the transfers for that day
        </p>
      )}

      {!loading && monthTotal === 0 && (
        <div className="bg-white border border-[#E2E8F0] flex flex-col items-center justify-center py-12 gap-3">
          <CalendarDays size={36} className="text-[#E2E8F0]" />
          <p className="text-sm text-[#94A3B8]">No transfers recorded in {format(currentMonth, "MMMM yyyy")}.</p>
        </div>
      )}

      {/* Selected date — transfer list */}
      {selectedDate && (
        <div className="bg-white border border-[#E2E8F0]">
          {/* Section header */}
          <div className="flex items-center justify-between px-5 py-3 bg-[#F9FAFB] border-b border-[#E2E8F0]">
            <div className="flex items-center gap-2">
              <CalendarDays size={14} className="text-[#1B2A6B]" />
              <span className="text-sm font-bold text-[#1B2A6B]">
                {format(selectedDate, "EEEE, dd MMM yyyy")}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-[#475569] font-semibold">
                {selectedDateTransfers.length} transfer{selectedDateTransfers.length !== 1 ? "s" : ""}
              </span>
              <button
                onClick={() => setSelectedDate(null)}
                className="text-xs text-[#94A3B8] hover:text-[#475569] font-medium transition-colors"
              >
                ✕ Close
              </button>
            </div>
          </div>

          {/* Transfer table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E2E8F0]">
                  {["#", "Route", "Customer", "Vehicle", "Items", "Status", ""].map(h => (
                    <th key={h} className="text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest text-[#475569] whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {selectedDateTransfers.map((t, idx) => (
                  <tr
                    key={t.id}
                    className={`border-b border-[#E2E8F0] last:border-b-0 ${idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}`}
                  >
                    <td className="px-4 py-3 text-[#94A3B8] text-xs font-mono">{idx + 1}</td>

                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-sm font-medium text-[#0F172A] whitespace-nowrap">
                        <MapPin size={12} className="text-[#94A3B8] shrink-0" />
                        <span className="max-w-[100px] truncate">{t.origin_label}</span>
                        <ArrowRight size={12} className="text-[#CBD5E1] shrink-0" />
                        <span className="max-w-[100px] truncate">{t.destination_label}</span>
                      </div>
                    </td>

                    <td className="px-4 py-3 text-[#475569] whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <User size={12} className="text-[#94A3B8] shrink-0" />
                        {t.customer_name || "—"}
                      </div>
                    </td>

                    <td className="px-4 py-3 text-[#475569] whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <Truck size={12} className="text-[#94A3B8] shrink-0" />
                        {t.vehicle_display || "—"}
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-[#475569]">
                        <Package size={12} className="text-[#94A3B8] shrink-0" />
                        {(t.load_ids || []).length}
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <StatusBadge status={t.status} />
                    </td>

                    <td className="px-4 py-3">
                      <button
                        onClick={() => setViewTransfer(t)}
                        className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#1B2A6B] hover:text-white border border-[#1B2A6B] hover:bg-[#1B2A6B] transition-colors px-3 py-1.5 rounded-sm whitespace-nowrap"
                      >
                        <Eye size={12} />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Transfer detail dialog */}
      <TransferDetailDialog
        open={!!viewTransfer}
        onClose={() => setViewTransfer(null)}
        transfer={viewTransfer}
      />
    </div>
  );
}
